/**
 * Cart utilities for building basket items from various product selection structures.
 *
 * Two main flows:
 *
 * 1. **Price display** — `buildCustomizeCartItems` / `computeCustomizeTotal` flatten all
 *    selections into a single items array. Used by the ATC button label and flyout subtotal.
 *
 * 2. **Sequential add-to-cart** — `buildPrimaryCartItem` + `buildChildCartItems` produce
 *    separate payloads for a two-step basket mutation:
 *      a) Add the primary (cover variant) product with `c_selectedProducts`,
 *         `c_selectedOptionsUI`, and KingGuard as `optionItems`.
 *      b) Add child products with `c_parentProductId` and `c_parentProductLineItemUUId`
 *         (UUID extracted from the response of step a).
 *
 * KingGuard is a Product Option configured on the cover variant (and inherited from
 * the master). It is always added as `optionItems` on the primary cover variant item,
 * never as a separate line item.
 */

import {resolveCommittedConfiguredImageUrl} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {basketLineLeadTimeFields, getProductEffectivePrice, getProductListPrice, isProductCustomFlagTrue} from '@amblique/king-living/app/utils/product-utils';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';

const SELECTED_OPTIONS_UI_WARN_LIMIT = 3900;

/**
 * Resolve the product/variant used to stamp `c_leadTime` on the primary basket line.
 * Mirrors PDP lead-time resolution (variant → master → linked set product).
 *
 * @param {Object|null} product - PDP master product
 * @param {string|null} coverId
 * @param {{mode?: string, productId?: string}|null} committedSelection
 * @param {Record<string, Object>|null} setProductsMap
 * @returns {Object|null}
 */
function resolvePrimaryLeadTimeProduct(product, coverId, committedSelection, setProductsMap) {
  const setProduct =
    committedSelection?.mode === 'set' && committedSelection?.productId ?
      setProductsMap?.[committedSelection.productId] :
      null;

  const variant =
    (coverId ? getSelectedVariantProduct(product, coverId) : null) ??
    (coverId && setProduct ? getSelectedVariantProduct(setProduct, coverId) : null);

  if (variant) {
    return variant;
  }

  if (setProduct && !coverId) {
    return setProduct;
  }

  return product;
}

/**
 * Resolve the KingGuard Product Option and its "active" (price > 0) and
 * "none" (price = 0) values from a cover variant's `.options` array.
 *
 * SFCC Product Options always apply a default value when `optionItems` is
 * omitted from the SCAPI addItemToBasket request. To truly opt out of
 * KingGuard, the "none" value must be sent explicitly.
 *
 * @param {Object|null} coverVariant
 * @returns {{option: Object, activeValue: Object|null, noneValue: Object|null}|null}
 */
function getKingGuardOptionAndValues(coverVariant) {
  const option = coverVariant?.options?.find((o) => o.id === 'kingGuard');

  if (!option?.id || !option?.values?.length) return null;

  const activeValue = option.values.find((v) => v.price > 0) || option.values[0];
  const noneValue = option.values.find((v) => v.price === 0 || v.price == null) || null;

  return {option, activeValue, noneValue};
}

/** @param {Object|null} coverVariant */
export function getKingGuardOptionValue(coverVariant) {
  return getKingGuardOptionAndValues(coverVariant)?.activeValue;
}

/**
 * Resolve which product object carries the KingGuard Product Option (and its
 * variant-specific pricing) for a given PDP context.
 *
 * SFCC Product Option prices can be overridden per-variant. SFRA enriches each
 * `c_allVariantsData` entry with that variant-level `options` array via
 * `coverVariantsHelper.buildProductOptions(dwVariant)`. The master also exposes
 * an `options` array via SCAPI `expand=options`, but the prices there are the
 * master defaults and miss any per-variant overrides.
 *
 * Prefer the cover variant when it actually carries options; fall back to the
 * master otherwise (no cover committed yet, or the variant entry was authored
 * without options). Same logic is mirrored in `<ProtectYourCover>` so the
 * checkbox label, the basket payload, and the displayed total all agree on
 * which product is the source of truth for the KingGuard price.
 *
 * @param {Object|null} masterProduct
 * @param {Object|null} coverVariant
 * @returns {Object|null}
 */
export function resolveKingGuardSourceProduct(masterProduct, coverVariant) {
  return coverVariant?.options?.length > 0 ? coverVariant : masterProduct;
}

/** @param {Object|null} coverVariant */
export function getKingGuardOptionItemsForBasket(coverVariant) {
  const resolved = getKingGuardOptionAndValues(coverVariant);

  if (!resolved?.activeValue?.id) return null;

  return [{optionId: resolved.option.id, optionValueId: resolved.activeValue.id}];
}

/**
 * Build optionItems that explicitly disable KingGuard by selecting the "none"
 * value (price = 0). Returns null when the option has no "none" value.
 *
 * @param {Object|null} coverVariant
 * @returns {Array|null}
 */
export function getKingGuardNoneOptionItemsForBasket(coverVariant) {
  const resolved = getKingGuardOptionAndValues(coverVariant);

  if (!resolved?.noneValue?.id) return null;

  return [{optionId: resolved.option.id, optionValueId: resolved.noneValue.id}];
}

// Hard-coded label fallback used only when neither the SCAPI product's
// option-value `name` nor the line item's localized `itemText` is available.
// Keyed by `optionId` so it stays generic if other Product Options are added.
const OPTION_LABEL_FALLBACKS = {
  kingGuard: 'KingGuard Fabric Protection',
};

const KING_GUARD_INCLUDED_ROW = {
  optionId: 'kingGuard',
  name: '',
  value: '',
  kingGuardIncluded: true,
};

/**
 * Strip a redundant `{optionId}: ` prefix that SCAPI/BM sometimes injects
 * into the localized option-value label (e.g. `"kingGuard: Add King Guard"`
 * → `"Add King Guard"`). Returns the input unchanged when no prefix is found.
 *
 * @param {string|null|undefined} text
 * @param {string|null|undefined} optionId
 * @returns {string}
 */
function stripOptionIdPrefix(text, optionId) {
  if (!text || !optionId) return text || '';
  const prefix = `${optionId}: `;

  return text.startsWith(prefix) ? text.substring(prefix.length) : text;
}

/**
 * Compute the display rows for a basket/order line item's SCAPI Product
 * Options (`optionItems`). Each row is rendered next to the other selected
 * options on the cart, checkout summary, and order confirmation surfaces.
 *
 * Format: `{option-value display name}: true`
 *   e.g. `Add King Guard: true`
 *
 * Behaviour:
 *   - Skips option values priced at 0 (e.g. KingGuard "No, don't add"), except
 *     set-bundled King Guard when `c_selectedOptionsUI.kingGuardFromSet` is set —
 *     those line items are often $0 but must show "KingGuard® included".
 *   - Resolves the LABEL from the option-value's display name (`product
 *     .options[].values[].name`, populated by SCAPI when products are
 *     fetched with `expand=options`, which is the `getProducts` default).
 *     Falls back to SCAPI's `optionItems[].itemText`, then to a hard-coded
 *     friendly label keyed by `optionId`, then to the raw `optionId`.
 *   - Strips any redundant `"{optionId}: "` prefix that BM data may include
 *     in the localized value name / itemText (live data observed for
 *     `kingGuard` carried the optionId in the displayed text).
 *   - Renders the value as the literal string `"true"` per design.
 *
 * Works for both new and pre-existing basket/order items because it reads
 * from the SCAPI response shape rather than relying on data persisted at
 * add-to-cart time.
 *
 * @param {Object|null} lineItem - SCAPI productItem (basket or order line).
 * @param {Object|null} [product] - SCAPI product fetched via `useProducts`,
 *   used to resolve the option-value's display name when available.
 * @returns {Array<{optionId: string, name: string, value: string, kingGuardIncluded?: boolean}>}
 */
export function getLineItemOptionRows(lineItem, product = null) {
  const optionItems = Array.isArray(lineItem?.optionItems) ? lineItem.optionItems : [];
  const kingGuardFromSet =
    parseSelectedOptionsUIPayload(lineItem?.c_selectedOptionsUI)?.kingGuardFromSet === true;

  if (optionItems.length === 0) {
    return kingGuardFromSet ? [KING_GUARD_INCLUDED_ROW] : [];
  }

  const rows = optionItems
    .filter((opt) => opt && ((opt.price ?? 0) > 0 || (kingGuardFromSet && opt.optionId === 'kingGuard')))
    .map((opt) => {
      if (kingGuardFromSet && opt.optionId === 'kingGuard') return KING_GUARD_INCLUDED_ROW;

      const productOption = product?.options?.find((o) => o.id === opt.optionId);
      const productValue = productOption?.values?.find((v) => v.id === opt.optionValueId);
      const rawLabel = productValue?.name ||
        opt.itemText ||
        OPTION_LABEL_FALLBACKS[opt.optionId] ||
        opt.optionId ||
        '';
      const label = stripOptionIdPrefix(rawLabel, opt.optionId);

      return {optionId: opt.optionId, name: label, value: 'true'};
    })
    .filter((row) => row.kingGuardIncluded || row.name);

  if (kingGuardFromSet && !rows.some((row) => row.optionId === 'kingGuard')) {
    rows.push(KING_GUARD_INCLUDED_ROW);
  }

  return rows;
}

export const KING_GUARD_INCLUDED_INTL = {
  defaultMessage: 'KingGuard® included',
  id: 'protect_your_cover.label.included',
};

/** Cart / confirmation line when KingGuard is opted in (not set-bundled). TM mark is per locale. */
export const KING_GUARD_CART_OPTION_INTL = {
  defaultMessage: 'KingGuard® Fabric Protection: Yes',
  id: 'protect_your_cover.cart.option',
};

/**
 * @param {import('react-intl').IntlShape} intl
 * @param {{optionId?: string, name?: string, value?: string, kingGuardIncluded?: boolean}} row
 * @returns {string}
 */
export function formatLineItemOptionRow(intl, row) {
  if (row?.kingGuardIncluded) {
    return intl.formatMessage(KING_GUARD_INCLUDED_INTL);
  }

  if (row?.optionId === 'kingGuard') {
    return intl.formatMessage(KING_GUARD_CART_OPTION_INTL);
  }

  return `${row?.name ?? ''}: ${row?.value ?? ''}`;
}

export function setProductIncludesKingGuard(linkedProduct) {
  return isProductCustomFlagTrue(linkedProduct?.c_selectedKingGuard);
}

// ---------------------------------------------------------------------------
// Sequential add-to-cart payload builders
// ---------------------------------------------------------------------------

/**
 * Build the `c_selectedOptionsUI` JSON string persisted on the primary basket line item.
 *
 * Captures the full UI selection state (container + product names, Cylindo config)
 * so the cart, order confirmation, and emails can reconstruct the configured product.
 *
 * @param {Object} selections - SelectionsMap from committed selection
 * @param {Object|null} [product] - Master product (for masterProductId)
 * @param {string|null} [cylindoUrl] - Final Cylindo viewer image URL (if available)
 * @param {Object|null} [kingGuardSourceProduct] - Product whose `.options` carries
 *   the KingGuard option + variant-specific price. Defaults to `product` (master).
 *   Pass the cover variant when its options array has been enriched by SFRA so
 *   the persisted `configuredTotal` includes the correct KingGuard price.
 * @param {boolean} [kingGuardFromSet]
 * @returns {string} JSON string (max ~4000 chars for SFCC Text attribute)
 */
export function formatSelectedOptionsUI(
  selections,
  product = null,
  cylindoUrl = null,
  kingGuardSourceProduct = null,
  kingGuardFromSet = false
) {
  if (!selections) return '{}';

  const options = [];

  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover' || stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (!slot?.productId) return;
      options.push({
        name: slot.recommendationName || stepId,
        value: slot.name || '',
        productId: slot.productId,
        containerId: stepId,
        isHidden: slot.isHidden === true,
      });
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (!slot?.productId) return;
      options.push({
        name: 'Optional add-on',
        value: slot.name || '',
        productId: slot.productId,
        containerId: slot.containerName || 'optionalAddOns',
        isHidden: slot.isHidden === true,
      });
    });
  }

  // Note: KingGuard is intentionally NOT pushed into this options array.
  // It rides along on the primary basket line as a SCAPI Product Option
  // (`optionItems`), and is rendered at runtime by
  // `getLineItemOptionRows()` so existing carts/orders also display it
  // (not just items added after this code shipped).

  const configuredTotal = computeCustomizeTotal(selections, product, true, kingGuardSourceProduct);
  const configuredListTotal = computeCustomizeListTotal(selections, product, true, kingGuardSourceProduct);

  const coverName = selections.cover?.name ?? null;

  const payload = {
    cylindoUrl: cylindoUrl ?? null,
    masterProductId: product?.id ?? null,
    coverName,
    configuredTotal: configuredTotal > 0 ? configuredTotal : null,
    configuredListTotal: configuredListTotal > 0 && configuredListTotal > configuredTotal ?
      configuredListTotal : null,
    options,
    ...(kingGuardFromSet ? {kingGuardFromSet: true} : {}),
  };

  const json = JSON.stringify(payload);

  if (json.length > SELECTED_OPTIONS_UI_WARN_LIMIT) {
    console.warn(
      `[formatSelectedOptionsUI] JSON payload is ${json.length} chars — ` +
      `exceeds ${SELECTED_OPTIONS_UI_WARN_LIMIT}. May be truncated by SFCC.`
    );
  }

  return json;
}

/**
 * Collect child product IDs from selections for the `c_selectedProducts` custom attribute
 * on the primary basket line item.
 *
 * Excludes `cover` (that IS the primary product) and KingGuard (added as a product
 * option via `optionItems`, not as a separate line item).
 *
 * @param {Object} selections - SelectionsMap
 * @returns {string[]}
 */
export function extractSelectedProductIds(selections) {
  if (!selections) return [];

  const ids = [];

  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover' || stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (slot?.productId) ids.push(slot.productId);
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (slot?.productId) ids.push(slot.productId);
    });
  }

  return ids;
}

/**
 * Product ids that would be added to the basket for the current customize selection
 * (cover primary line + option / add-on children). Used for warehouse inventory checks on PDP.
 *
 * @param {Object|null|undefined} selections - SelectionsMap
 * @returns {string[]}
 */
export function collectPdpLineItemProductIds(selections) {
  if (!selections) {
    return [];
  }

  const ids = new Set(extractSelectedProductIds(selections));
  const coverId = selections.cover?.productId ?? selections.cover?.variantId;

  if (coverId) {
    ids.add(coverId);
  }

  return [...ids];
}

/**
 * @param {(add: (id: string|null|undefined) => void) => void} collect
 * @returns {string[]}
 */
function collectUniqueProductIds(collect) {
  const ids = [];
  const seen = new Set();

  collect((id) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });

  return ids;
}

/**
 * Product IDs from a single customize add-to-cart batch for upsell link resolution.
 * Includes predefined set SKU (when applicable) and all selection line items.
 *
 * @param {Object} params
 * @param {Object|null|undefined} params.selections - SelectionsMap
 * @param {{mode?: string, productId?: string}|null|undefined} params.committedSelection
 * @param {string|null|undefined} params.primaryProductId
 * @returns {string[]}
 */
export function collectUpsellSourceProductIds({
  selections,
  committedSelection,
  primaryProductId,
}) {
  return collectUniqueProductIds((add) => {
    if (committedSelection?.mode === 'set' && committedSelection?.productId) {
      add(committedSelection.productId);
    }

    collectPdpLineItemProductIds(selections).forEach(add);
    add(primaryProductId);
  });
}

/**
 * Product IDs from a simple add-to-cart `itemsAdded` payload.
 *
 * @param {Array<{product?: Object, variant?: {productId?: string}}>} [itemsAdded]
 * @returns {string[]}
 */
export function collectUpsellSourceProductIdsFromItemsAdded(itemsAdded = []) {
  return collectUniqueProductIds((add) => {
    itemsAdded.forEach(({product, variant}) => {
      add(variant?.productId ?? product?.productId ?? product?.id);
    });
  });
}

/**
 * @param {Array<{type?: string, targetProductId?: string}>} [productLinks]
 * @returns {string[]}
 */
export function getUpSellTargetIds(productLinks = []) {
  return productLinks
    .filter((link) => link.type === 'up_sell')
    .map((link) => link.targetProductId)
    .filter(Boolean);
}

/**
 * Merge up_sell target IDs from source products (source order, then link order).
 *
 * @param {string[]} sourceProductIds
 * @param {Array<{id?: string, productId?: string, productLinks?: Array}>} sourceProducts
 * @param {number} maxItems
 * @returns {string[]}
 */
export function mergeUpSellTargetIds(sourceProductIds, sourceProducts, maxItems) {
  const productsById = new Map();

  (sourceProducts ?? []).forEach((product) => {
    const id = product?.id ?? product?.productId;

    if (id) {
      productsById.set(id, product);
    }
  });

  return collectUniqueProductIds((add) => {
    sourceProductIds.forEach((sourceId) => {
      getUpSellTargetIds(productsById.get(sourceId)?.productLinks).forEach(add);
    });
  }).slice(0, maxItems);
}

/**
 * @template {{id?: string, productId?: string}} T
 * @param {T[]} products
 * @param {string[]} orderedIds
 * @returns {T[]}
 */
export function sortProductsByIdOrder(products, orderedIds) {
  if (!products.length || !orderedIds.length) {
    return products;
  }

  const order = new Map(orderedIds.map((id, index) => [id, index]));

  return [...products].sort((a, b) => {
    const aIndex = order.get(a?.id ?? a?.productId) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = order.get(b?.id ?? b?.productId) ?? Number.MAX_SAFE_INTEGER;

    return aIndex - bIndex;
  });
}

/**
 * Product IDs for the upsell page from navigation state.
 *
 * @param {Object} [params]
 * @param {string[]} [params.addedProductIds]
 * @param {Array<{product?: Object, variant?: {productId?: string}}>} [params.itemsAdded]
 * @param {Object} [params.product]
 * @returns {string[]}
 */
export function resolveUpsellNavigationProductIds({
  addedProductIds,
  itemsAdded,
  product,
} = {}) {
  if (addedProductIds?.length) {
    return addedProductIds;
  }

  const fromItems = collectUpsellSourceProductIdsFromItemsAdded(itemsAdded);

  if (fromItems.length) {
    return fromItems;
  }

  const fallbackId =
    itemsAdded?.[0]?.variant?.productId ?? product?.productId ?? product?.id ?? null;

  return fallbackId ? [fallbackId] : [];
}

/**
 * Build the primary basket item for step 1 of the sequential ATC flow.
 *
 * Works for both:
 * - **Complex PDP** — primary = selected `cover` variant; price comes from the cover.
 * - **Simple PDP w/ configurable options** — no cover step exists, so primary = master
 *   product itself; price comes from the master.
 *
 * Includes:
 * - `c_selectedProducts` — IDs of all child recommendation products
 * - `c_selectedOptionsUI` — JSON payload for cart/order display
 * - `optionItems` — KingGuard as a product option (Complex only — Simple master usually
 *   has no `kingGuard` option configured, in which case this is a no-op)
 *
 * @param {Object} selections - SelectionsMap (KINDW-1119 §A1: Simple PDP omits `cover`)
 * @param {Object|null} [kingGuardSourceProduct] - Product whose `.options` contains the KingGuard option
 * @param {Object|null} [product] - Master product. Used for `masterProductId` and as the
 *   primary product fallback when no cover is selected (Simple PDP).
 * @param {string|null} [cylindoUrl] - Cylindo image URL
 * @param {{mode?: string, productId?: string}|null} [committedSelection]
 * @param {Record<string, Object>|null} [setProductsMap]
 * @returns {Object|null} Primary cart item or null when no primary product can be resolved
 */
export function buildPrimaryCartItem(
  selections,
  kingGuardSourceProduct = null,
  product = null,
  cylindoUrl = null,
  committedSelection = null,
  setProductsMap = null
) {
  if (!selections) return null;

  const coverId = selections.cover?.productId ?? selections.cover?.variantId;
  const masterId = product?.id ?? product?.productId ?? null;
  const primaryId = coverId ?? masterId;

  if (!primaryId) return null;

  // Cover (Complex) sets price on the selection itself; Simple uses master `price`.
  const coverPrice = selections.cover?.salePrice ?? selections.cover?.price;
  const masterPrice = !coverId ? (product?.price ?? null) : null;
  const price = coverPrice ?? masterPrice;

  const linkedSet = committedSelection?.mode === 'set' && committedSelection?.productId ?
    setProductsMap?.[committedSelection.productId] :
    null;
  const setIncludesKingGuard = setProductIncludesKingGuard(linkedSet);
  const kgEnabled =
    isProductCustomFlagTrue(selections.optionalAddOns?.kingGuard?.enabled) || setIncludesKingGuard;
  const kingGuardFromSet = committedSelection?.mode === 'set' && setIncludesKingGuard;

  const item = {
    productId: primaryId,
    quantity: 1,
    ...(price > 0 ? {price} : {}),
    c_selectedProducts: extractSelectedProductIds(selections),
    c_selectedOptionsUI: formatSelectedOptionsUI(
      selections,
      product,
      cylindoUrl,
      kingGuardSourceProduct,
      kingGuardFromSet
    ),
  };

  const kgResolved = getKingGuardOptionAndValues(kingGuardSourceProduct);

  if (kgResolved) {
    if (kgEnabled && kgResolved.activeValue?.id) {
      item.optionItems = [{optionId: 'kingGuard', optionValueId: kgResolved.activeValue.id}];
    } else if (kgResolved.noneValue?.id) {
      item.optionItems = [{optionId: 'kingGuard', optionValueId: kgResolved.noneValue.id}];
    }
  }

  Object.assign(
    item,
    basketLineLeadTimeFields(
      resolvePrimaryLeadTimeProduct(product, coverId, committedSelection, setProductsMap)
    )
  );

  return item;
}

/**
 * Whether configured-image resolution must have a cover slot (Complex customize-your-own).
 * Predefined sets use the same resolver as set cards (`requireCover: false`).
 *
 * @param {{ isSimplePdpFlow?: boolean, committedMode?: string|null }} options
 * @returns {boolean}
 */
export function requiresCoverForConfiguredImage({isSimplePdpFlow = false, committedMode = null} = {}) {
  if (isSimplePdpFlow) return false;
  if (committedMode === 'set') return false;

  return true;
}

export function parseSelectedOptionsUIPayload(cSelectedOptionsUI) {
  if (!cSelectedOptionsUI) return null;

  try {
    return JSON.parse(cSelectedOptionsUI);
  } catch {
    return null;
  }
}

/**
 * Unique basket line `productId`s plus `masterProductId` from `c_selectedOptionsUI`.
 * @param {object[]} [lines]
 * @param {string[]} [additionalIds]
 * @returns {string[]}
 */
export function collectBasketCatalogProductIds(lines = [], additionalIds = []) {
  const ids = new Set(additionalIds.filter(Boolean));

  lines.forEach((item) => {
    if (item?.productId) {
      ids.add(item.productId);
    }

    const masterProductId = parseSelectedOptionsUIPayload(item?.c_selectedOptionsUI)?.masterProductId;

    if (masterProductId) {
      ids.add(masterProductId);
    }
  });

  return [...ids];
}

/**
 * Catalog product for line-item merchandising name and shortDescription.
 * Uses master when `c_selectedOptionsUI.masterProductId` exists; otherwise the line SKU.
 * @param {object|null|undefined} lineItem
 * @param {Record<string, object>|null|undefined} productsByProductId
 * @returns {object|null}
 */
export function resolveBasketLineDisplayProduct(lineItem, productsByProductId) {
  if (!lineItem || !productsByProductId) return null;

  const masterProductId = parseSelectedOptionsUIPayload(lineItem.c_selectedOptionsUI)?.masterProductId;

  if (masterProductId) {
    return productsByProductId[masterProductId] ?? null;
  }

  return productsByProductId[lineItem.productId] ?? null;
}

/**
 * Line-item merchandising name and shortDescription (master or standalone line SKU).
 * @param {object|null|undefined} lineItem
 * @param {Record<string, object>|null|undefined} productsByProductId
 * @param {object|null|undefined} lineProduct - Line SKU catalog record
 * @returns {{name: string, shortDescription: string|null}}
 */
export function resolveBasketLineMerchandisingCopy(lineItem, productsByProductId, lineProduct) {
  if (!productsByProductId) {
    return {
      name: lineItem?.productName ?? lineProduct?.name ?? '',
      shortDescription: lineProduct?.shortDescription ?? null,
    };
  }

  const displayProduct = resolveBasketLineDisplayProduct(lineItem, productsByProductId);

  return {
    name: displayProduct?.name ?? lineItem?.productName ?? lineProduct?.name ?? '',
    shortDescription: displayProduct?.shortDescription ?? null,
  };
}

/**
 * @param {string|undefined} cSelectedOptionsUI
 * @returns {string|null}
 */
export function parseCylindoUrlFromSelectedOptionsUI(cSelectedOptionsUI) {
  return parseSelectedOptionsUIPayload(cSelectedOptionsUI)?.cylindoUrl ?? null;
}

/**
 * Hero image for customize ATC / upsell — resolver + line-item JSON fallback.
 *
 * @param {Object} params
 * @param {Object|null} params.masterProduct
 * @param {Object|null|undefined} params.selections
 * @param {Object|null|undefined} params.primaryItem
 * @param {boolean} [params.requireCover]
 * @returns {string|null}
 */
export function resolveCustomizeAtcConfiguredImageUrl({
  masterProduct,
  selections,
  primaryItem,
  requireCover = true,
}) {
  const fromResolver = resolveCommittedConfiguredImageUrl(masterProduct, selections, {requireCover});

  if (fromResolver) return fromResolver;

  return parseCylindoUrlFromSelectedOptionsUI(primaryItem?.c_selectedOptionsUI);
}

/**
 * Read configured hero URL from a basket line (primary customize parent).
 *
 * @param {Object|null|undefined} basket
 * @param {string|null|undefined} lineProductId
 * @returns {string|null}
 */
export function getConfiguredImageUrlFromBasketLine(basket, lineProductId) {
  if (!basket?.productItems?.length || !lineProductId) return null;

  const lineItem = basket.productItems.find((item) => item.productId === lineProductId);

  if (!lineItem) return null;

  return parseCylindoUrlFromSelectedOptionsUI(lineItem.c_selectedOptionsUI);
}

/**
 * Upsell navigation payload after customize-journey add-to-cart.
 *
 * @param {Object} params
 * @param {Object} params.product - Master PDP product
 * @param {string|null} params.configuredImageUrl - Resolved hero image (Cylindo layers)
 * @param {string} params.primaryProductId - Primary basket line product id
 * @param {number} params.total - Sale total for the configuration
 * @param {number} params.listTotal - List total for strikethrough when on sale
 * @param {number} params.totalQty - Primary + child line count
 * @param {Object|null|undefined} params.selections - SelectionsMap for upsell source ids
 * @param {{mode?: string, productId?: string}|null|undefined} params.committedSelection
 * @returns {Object}
 */
export function buildCustomizeAtcSuccessPayload({
  product,
  configuredImageUrl,
  primaryProductId,
  total,
  listTotal,
  totalQty,
  selections,
  committedSelection,
}) {
  const isOnSale = listTotal > 0 && listTotal > total;

  return {
    addedProductIds: collectUpsellSourceProductIds({
      selections,
      committedSelection,
      primaryProductId,
    }),
    configuredImageUrl,
    product,
    itemsAdded: [{
      product,
      variant: {productId: primaryProductId, variationValues: {}},
      quantity: totalQty,
      priceData: {
        currentPrice: total,
        listPrice: isOnSale ? listTotal : null,
        isOnSale,
        isRange: false,
        isMaster: false,
        isASet: false,
      },
    }],
    selectedQuantity: totalQty,
  };
}

/**
 * Build child (recommendation + add-on) basket items for step 2 of the
 * sequential ATC flow.
 *
 * Each child carries `c_parentProductId` so the cart UI can group them visually.
 *  - Complex PDP → parent = cover variant id (resolved from `selections.cover`).
 *  - Simple PDP w/ configurable options → parent = master product id (passed via
 *    `fallbackParentProductId` since there is no cover step).
 * The caller adds `c_parentProductLineItemUUId` after extracting the UUID from
 * the step-1 response.
 *
 * KingGuard is NOT included here — it lives as a product option (`optionItems`)
 * on the primary basket item.
 *
 * @param {Object} selections - SelectionsMap
 * @param {string|null} [fallbackParentProductId] - Used as `c_parentProductId` when
 *   there is no cover (Simple PDP). Typically the master product id.
 * @returns {Array<Object>}
 */
export function buildChildCartItems(selections, fallbackParentProductId = null) {
  if (!selections) return [];

  const items = [];
  const coverId = selections.cover?.productId ?? selections.cover?.variantId;
  const parentId = coverId ?? fallbackParentProductId ?? null;

  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover' || stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (!slot?.productId) return;
      items.push({
        productId: slot.productId,
        quantity: 1,
        ...(slot.price > 0 ? {price: slot.price} : {}),
        ...(parentId ? {c_parentProductId: parentId} : {}),
      });
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (!slot?.productId) return;
      items.push({
        productId: slot.productId,
        quantity: 1,
        ...(slot.price > 0 ? {price: slot.price} : {}),
        ...(parentId ? {c_parentProductId: parentId} : {}),
      });
    });
  }

  return items;
}

/**
 * Basket line items from committed customize selection (cover + slots + addons; KingGuard as cover `optionItems`).
 * @param {import('../context/customize-journey-context').SelectionsMap} selections
 * @param {Object|null} [coverVariant]
 */
export function buildCustomizeCartItems(selections, coverVariant = null) {
  const items = [];

  if (!selections) return items;

  const coverId = selections.cover?.productId ?? selections.cover?.variantId;

  const kgOptionItems = selections.optionalAddOns?.kingGuard?.enabled === true ?
    getKingGuardOptionItemsForBasket(coverVariant) :
    getKingGuardNoneOptionItemsForBasket(coverVariant);

  if (coverId) {
    items.push({
      productId: coverId,
      quantity: 1,
      ...(kgOptionItems ? {optionItems: kgOptionItems} : {}),
      ...basketLineLeadTimeFields(coverVariant),
    });
  }

  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover') return;
    if (stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (!slot?.productId) return;
      items.push({
        productId: slot.productId,
        quantity: 1,
        ...(coverId ? {c_parentProductId: coverId} : {}),
      });
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (!slot?.productId) return;
      items.push({
        productId: slot.productId,
        quantity: 1,
        ...(coverId ? {c_parentProductId: coverId} : {}),
      });
    });
  }

  return items;
}

/**
 * Customize-path display price (sticky strip is source of truth).
 * Configured total when > 0; otherwise master starting price.
 *
 * @param {Object} params
 * @param {boolean} [params.useCustomizePath=true]
 * @param {number} [params.complexTotal=0]
 * @param {number} [params.masterProductPrice=0]
 * @param {number} [params.nonCustomizePrice=0]
 * @returns {number}
 */
export function resolveCustomizeAtcDisplayPrice({
  useCustomizePath = true,
  complexTotal = 0,
  masterProductPrice = 0,
  nonCustomizePrice = 0,
} = {}) {
  if (!useCustomizePath) return nonCustomizePrice ?? 0;
  if (complexTotal > 0) return complexTotal;

  return masterProductPrice > 0 ? masterProductPrice : 0;
}

/**
 * Total for committed selection. Use `includeKingGuard=false` for flyout subtotal.
 * @param {import('../context/customize-journey-context').SelectionsMap} selections
 * @param {Object|null} [productOrCoverVariant] - Base product (simple PDP) or cover variant (complex PDP)
 * @param {boolean} [includeKingGuard=true]
 * @param {Object|null} [kingGuardSourceProduct] - Optional product whose `.options` carries
 *   the KingGuard option + variant-specific price. Defaults to `productOrCoverVariant`,
 *   which is correct for Complex PDPs (cover variant). Simple PDPs must pass the resolved
 *   variant explicitly because their `productOrCoverVariant` is the master, and the master's
 *   option model misses per-variant KingGuard price overrides written by SFRA into
 *   `c_allVariantsData[].options`. See `resolveKingGuardSourceProduct`.
 */
export function computeCustomizeTotal(selections, productOrCoverVariant = null, includeKingGuard = true, kingGuardSourceProduct = null, optionPriceMap = null) {
  if (!selections) return 0;

  let total = 0;

  if (selections.cover) {
    // Complex PDP: cover selection has the base price. Prefer the promotion-aware
    // effective price from the fetched map (SCAPI productPromotions) so the CTA
    // total matches the set-card total; fall back to the cover's sale/list price.
    const coverId = selections.cover.productId;
    const mappedCover =
      coverId && optionPriceMap && optionPriceMap[coverId] != null ? optionPriceMap[coverId] : null;

    total += mappedCover ?? selections.cover.salePrice ?? selections.cover.price ?? 0;
  } else if (productOrCoverVariant) {
    // Simple PDP: no cover selection, use main product price as base. Prefer the
    // after-promotion effective price (SCAPI productPromotions) so the coverless base
    // reflects promotions just like the Complex cover does.
    const basePrice = getProductEffectivePrice(productOrCoverVariant) ?? productOrCoverVariant.price ?? 0;

    total += basePrice;
  }

  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover') return;
    if (stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (!slot?.productId) return;
      // Prefer the promotion-aware price from the fetched map so the total reflects
      // prices AFTER promotions; fall back to the slot's price.
      const mapped = optionPriceMap && optionPriceMap[slot.productId] != null ? optionPriceMap[slot.productId] : null;

      total += mapped ?? slot.price ?? 0;
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (!slot?.productId) return;
      const mapped = optionPriceMap && optionPriceMap[slot.productId] != null ? optionPriceMap[slot.productId] : null;

      total += mapped ?? slot.price ?? 0;
    });
  }

  if (includeKingGuard && selections.optionalAddOns?.kingGuard?.enabled === true) {
    const kgValue = getKingGuardOptionValue(kingGuardSourceProduct ?? productOrCoverVariant);

    if (kgValue?.price != null) {
      total += kgValue.price;
    }
  }

  return total;
}

/**
 * List (non-discounted) total for committed selection.
 * Uses `cover.price` (list price) instead of `cover.salePrice` (sale price),
 * and `slot.listPrice ?? slot.price` for option slots.
 * Returns 0 when no list-level discount data exists.
 *
 * @param {import('../context/customize-journey-context').SelectionsMap} selections
 * @param {Object|null} [productOrCoverVariant] - Base product (simple PDP) or cover variant (complex PDP)
 * @param {boolean} [includeKingGuard=true]
 * @param {Object|null} [kingGuardSourceProduct] - See `computeCustomizeTotal`.
 */
export function computeCustomizeListTotal(selections, productOrCoverVariant = null, includeKingGuard = true, kingGuardSourceProduct = null, listPriceMap = null) {
  if (!selections) return 0;

  let total = 0;
  let hasListData = false;

  if (selections.cover) {
    // Complex PDP: cover selection has base price data
    const coverId = selections.cover.productId;
    const listPrice =
      selections.cover.price ??
      (coverId && listPriceMap && listPriceMap[coverId] != null ? listPriceMap[coverId] : null);
    const salePrice = selections.cover.salePrice;

    if (listPrice != null && salePrice != null && listPrice > salePrice) {
      total += listPrice;
      hasListData = true;
    } else {
      total += salePrice ?? listPrice ?? 0;
    }
  } else if (productOrCoverVariant) {
    // Simple PDP: no cover selection — use the master product's list price (parsed from
    // `c_priceProcessed.listPrice` by getProductListPrice) when it actually beats the
    // sale price on `product.price`. Falls back to `product.price` so master products
    // without a list price still produce a sensible base total.
    //
    // Without this branch, the base would always equal `product.price` (sale), the
    // saving would compute to 0, and the shared TotalPriceBlock would silently skip
    // the "Saving $X" badge + "Total package value" line — which is exactly the bug
    // observed on Simple-PDP products like ETO-DESK_CMPCT_SEL_PKG_TYPE_I_V3 where
    // c_priceProcessed = {salePrice: 50, listPrice: 100}.
    const salePrice = productOrCoverVariant.price ?? 0;
    // Effective floor (after promotions) — keeps saving = was − effective consistent
    // with computeCustomizeTotal's promo-aware coverless base.
    const effective = getProductEffectivePrice(productOrCoverVariant) ?? salePrice;
    const listFromProcessed = getProductListPrice(productOrCoverVariant);
    const wasPrice = listFromProcessed != null && listFromProcessed > salePrice ? listFromProcessed : salePrice;

    if (wasPrice > effective) {
      total += wasPrice;
      hasListData = true;
    } else {
      total += effective;
    }
  }

  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover') return;
    if (stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot) => {
      if (!slot?.productId) return;
      const slotListPrice =
        slot.listPrice ?? (listPriceMap && listPriceMap[slot.productId] != null ? listPriceMap[slot.productId] : null);

      if (slotListPrice != null && slotListPrice > (slot.price ?? 0)) {
        total += slotListPrice;
        hasListData = true;
      } else {
        total += slot.price ?? 0;
      }
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (!slot?.productId) return;
      if (slot.listPrice != null && slot.listPrice > (slot.price ?? 0)) {
        total += slot.listPrice;
        hasListData = true;
      } else {
        total += slot.price ?? 0;
      }
    });
  }

  if (includeKingGuard && selections.optionalAddOns?.kingGuard?.enabled === true) {
    const kgValue = getKingGuardOptionValue(kingGuardSourceProduct ?? productOrCoverVariant);

    if (kgValue?.price != null) {
      total += kgValue.price;
    }
  }

  return hasListData ? total : 0;
}

/**
 * Line items created after `existingItemIds` snapshot (this customize ATC attempt).
 *
 * @param {Object|null|undefined} basket
 * @param {Set<string>} existingItemIds
 * @returns {Array<Object>}
 */
export function getNewBasketLineItems(basket, existingItemIds) {
  return (basket?.productItems ?? []).filter((li) => !existingItemIds.has(li.itemId));
}

/**
 * Remove all line items from the current customize ATC attempt (children first, then parents).
 *
 * @param {string} basketId
 * @param {Object|null|undefined} basket
 * @param {Set<string>} existingItemIds - Item IDs present before this ATC started
 * @param {(params: {parameters: {basketId: string, itemId: string}}) => Promise<unknown>} removeItemFromBasket
 * @returns {Promise<void>}
 */
export async function rollbackNewCustomizeBasketLineItems(
  basketId,
  basket,
  existingItemIds,
  removeItemFromBasket
) {
  const newItems = getNewBasketLineItems(basket, existingItemIds);

  if (!newItems.length) return;

  const children = newItems.filter((li) => li.c_parentProductLineItemUUId);
  const parents = newItems.filter((li) => !li.c_parentProductLineItemUUId);

  for (const li of children) {
    await removeItemFromBasket({parameters: {basketId, itemId: li.itemId}});
  }

  for (const li of parents) {
    await removeItemFromBasket({parameters: {basketId, itemId: li.itemId}});
  }
}

// ---------------------------------------------------------------------------
// Basket display helpers — group complex line items for cart / checkout UI
// ---------------------------------------------------------------------------

/**
 * Group complex (guided PDP) line items for display purposes.
 *
 * Child items (those with `c_parentProductLineItemUUId`) are hidden from the
 * visible list.  Their prices are rolled up into the parent item so the customer
 * sees a single consolidated price for the configured product.
 *
 * Non-complex items pass through unchanged.
 *
 * @param {Array<Object>} productItems - `basket.productItems` or `order.productItems`
 * @returns {{
 *   visibleItems: Array<Object>,
 *   childItemIds: Set<string>,
 *   childrenByParent: Object<string, Array<Object>>
 * }}
 */
export function groupComplexLineItems(productItems) {
  if (!productItems?.length) {
    return {visibleItems: [], childItemIds: new Set(), childrenByParent: {}};
  }

  const childItemIds = new Set();
  const childrenByParent = {};
  const childrenByParentProductId = {};

  productItems.forEach((item) => {
    const parentUUID = item.c_parentProductLineItemUUId;
    const parentProductId = item.c_parentProductId;

    if (parentUUID || parentProductId) {
      childItemIds.add(item.itemId);

      if (parentUUID) {
        if (!childrenByParent[parentUUID]) {
          childrenByParent[parentUUID] = [];
        }

        childrenByParent[parentUUID].push(item);
      }

      if (parentProductId) {
        if (!childrenByParentProductId[parentProductId]) {
          childrenByParentProductId[parentProductId] = [];
        }

        childrenByParentProductId[parentProductId].push(item);
      }
    }
  });

  // Count visible parents per productId so we can apply the productId fallback
  // safely. When the same configurable SKU appears multiple times in the basket
  // (e.g. user adds the same complex product 3 times with different options),
  // attaching every child of that productId to every parent would cause one
  // remove/update to cascade across all instances — see KINDW cart-removal bug.
  const visibleParentCountByProductId = {};

  productItems.forEach((item) => {
    if (!childItemIds.has(item.itemId)) {
      visibleParentCountByProductId[item.productId] =
        (visibleParentCountByProductId[item.productId] ?? 0) + 1;
    }
  });

  // Position-based fallback (final tier): SFCC assigns NEW line item UUIDs
  // during basket→order placement, so `c_parentProductLineItemUUId` on order
  // children references the OLD basket parent UUID and won't match any
  // visible order itemId. The productId fallback above also fails when the
  // same configurable SKU appears more than once on the order (count > 1).
  // Both BM-shipment ordering and SCAPI `productItems[]` preserve insertion
  // order, so we walk the array sequentially and attach each hidden child to
  // the most recent preceding visible parent. This is only consulted when
  // the UUID and unique-productId strategies above both come up empty.
  const positionalChildrenByParentItemId = {};
  let positionalCurrentParent = null;

  productItems.forEach((item) => {
    if (!childItemIds.has(item.itemId)) {
      positionalCurrentParent = item;
      positionalChildrenByParentItemId[item.itemId] = [];
    } else if (positionalCurrentParent) {
      positionalChildrenByParentItemId[positionalCurrentParent.itemId].push(item);
    }
  });

  // Sum SCAPI Product Option contributions (e.g. KingGuard) on a line.
  // SCAPI's `productItem.price` honours the explicit `price` we send in the
  // addItemToBasket payload and does NOT add the `optionItems[].price` on top
  // (the basket-level `productSubTotal` does include them). So we have to
  // fold the option contributions into the displayed line totals here, or
  // the cart/checkout/confirmation surfaces report a line total that's
  // smaller than the basket subtotal.
  //
  // Field-aware: `optionItems` carry their own promotion adjustments, so the
  // option contributes its LIST price (`price`) to the strikethrough/base
  // total but its DISCOUNTED price (`priceAfterItemDiscount` /
  // `priceAfterOrderDiscount`) to the after-promotion totals. Using `price`
  // for everything overstates the sale total by the option promo (e.g.
  // KingGuard 169 list vs 152.1 after a 10% promo). Falls back to the less-
  // discounted field when SCAPI omits one.
  const sumOptionItems = (productItem, field = 'price') => (productItem?.optionItems ?? [])
    .reduce((sum, opt) => {
      if (!opt) return sum;

      let value;

      if (field === 'priceAfterItemDiscount') {
        value = opt.priceAfterItemDiscount ?? opt.price ?? 0;
      } else if (field === 'priceAfterOrderDiscount') {
        value = opt.priceAfterOrderDiscount ?? opt.priceAfterItemDiscount ?? opt.price ?? 0;
      } else {
        value = opt.price ?? 0;
      }

      return sum + value;
    }, 0);

  const visibleItems = productItems
    .filter((item) => !childItemIds.has(item.itemId))
    .map((item) => {
      // Primary: match by line item UUID. Reliable for baskets and for orders
      // when SFCC preserves the original UUID on `c_parentProductLineItemUUId`.
      // Fallback 1: match by product ID, but ONLY when this is the single visible
      // parent for that SKU. Otherwise the same children would be attached to
      // every duplicate parent and remove/update would cascade across instances.
      // Fallback 2 (positional): handles the order-confirmation case where SFCC
      // re-issued itemIds so UUID match fails, AND the configurable SKU appears
      // more than once so the productId fallback is skipped. Children that fell
      // through both prior strategies still appear sequentially after their
      // parent in the productItems[] array.
      const uuidChildren = childrenByParent[item.itemId];
      const productIdFallbackChildren = visibleParentCountByProductId[item.productId] === 1 ?
        childrenByParentProductId[item.productId] :
        null;
      const positionalChildren = positionalChildrenByParentItemId[item.itemId];
      const children = uuidChildren?.length ?
        uuidChildren :
        (productIdFallbackChildren?.length ?
          productIdFallbackChildren :
          positionalChildren);

      // Non-complex (no children) — still fold in the line's own optionItems
      // so simple products with paid Product Options show the full line total.
      if (!children?.length) {
        const optionsBaseTotal = sumOptionItems(item, 'price');

        if (optionsBaseTotal === 0) return item;

        const optionsAfterItemTotal = sumOptionItems(item, 'priceAfterItemDiscount');
        const optionsAfterOrderTotal = sumOptionItems(item, 'priceAfterOrderDiscount');

        return {
          ...item,
          price: (item.price ?? 0) + optionsBaseTotal,
          priceAfterItemDiscount:
            (item.priceAfterItemDiscount ?? item.price ?? 0) + optionsAfterItemTotal,
          priceAfterOrderDiscount:
            (item.priceAfterOrderDiscount ?? item.price ?? 0) + optionsAfterOrderTotal,
          basePrice: (item.basePrice ?? item.price ?? 0) + optionsBaseTotal,
        };
      }

      const sumField = (field, fallback) => children.reduce((sum, child) => sum + (child[field] ?? child[fallback] ?? 0), 0);

      // Total of all Product Option contributions across the parent and any
      // children. In practice only the parent (cover variant) carries the
      // KingGuard option, but summing across the whole group keeps this
      // future-proof for additional per-child options. Field-aware so the
      // option promo (e.g. KingGuard 169 → 152.1) is reflected in the
      // after-discount totals and NOT in the list/base total.
      const sumOptionItemsGroup = (field) => sumOptionItems(item, field) +
        children.reduce((sum, child) => sum + sumOptionItems(child, field), 0);

      const optionsBaseTotal = sumOptionItemsGroup('price');
      const optionsAfterItemTotal = sumOptionItemsGroup('priceAfterItemDiscount');
      const optionsAfterOrderTotal = sumOptionItemsGroup('priceAfterOrderDiscount');

      const sfccPrice = (item.price ?? 0) + sumField('price', 'price') + optionsBaseTotal;
      const sfccAfterItemDiscount =
        (item.priceAfterItemDiscount ?? item.price ?? 0) +
        sumField('priceAfterItemDiscount', 'price') +
        optionsAfterItemTotal;
      const sfccAfterOrderDiscount =
        (item.priceAfterOrderDiscount ?? item.price ?? 0) +
        sumField('priceAfterOrderDiscount', 'price') +
        optionsAfterOrderTotal;

      let configuredTotal = null;
      let configuredListTotal = null;

      try {
        const uiPayload = item.c_selectedOptionsUI ?
          JSON.parse(item.c_selectedOptionsUI) :
          null;

        configuredTotal = uiPayload?.configuredTotal ?? null;
        configuredListTotal = uiPayload?.configuredListTotal ?? null;
      } catch { /* ignore parse errors */ }

      // Prefer SFCC prices (which include promotion adjustments);
      // fall back to configuredTotal only when SFCC prices are $0
      // (missing price book entries).
      const usesFallback = sfccPrice === 0 && configuredTotal > 0;

      // Aggregate priceAdjustments from children so the "Promotions"
      // line shows the total discount across the whole group.
      const parentAdj = item.priceAdjustments ?? [];
      const childAdj = children.flatMap((c) => c.priceAdjustments ?? []);
      const mergedAdjustments = parentAdj.length || childAdj.length ?
        [...parentAdj, ...childAdj] :
        undefined;

      return {
        ...item,
        price: usesFallback ? configuredTotal : sfccPrice,
        priceAfterItemDiscount: usesFallback ?
          configuredTotal : sfccAfterItemDiscount,
        priceAfterOrderDiscount: usesFallback ?
          configuredTotal : sfccAfterOrderDiscount,
        basePrice: configuredListTotal ?? (usesFallback ?
          configuredTotal : sfccPrice),
        ...(mergedAdjustments ? {priceAdjustments: mergedAdjustments} : {}),
        _isComplexParent: true,
        _childItems: children,
        _configuredTotal: configuredTotal,
        _configuredListTotal: configuredListTotal,
      };
    });

  return {visibleItems, childItemIds, childrenByParent};
}

/** Parent + children in the same configuration group; `removalOrder` is children-first. */
export function resolveBasketRemovalGroup(lineItem, productItems = []) {
  if (!lineItem?.itemId) {
    return {parent: null, children: [], removalOrder: []};
  }

  if (!productItems?.length) {
    return {parent: lineItem, children: [], removalOrder: [lineItem]};
  }

  let parent = lineItem;
  const parentLineUUID = lineItem.c_parentProductLineItemUUId;

  if (parentLineUUID) {
    const linkedParent = productItems.find((li) => li.itemId === parentLineUUID);

    if (linkedParent) {
      parent = linkedParent;
    }
  }

  const {visibleItems} = groupComplexLineItems(productItems);
  const groupedParent = visibleItems.find((item) => item.itemId === parent.itemId);
  const children = groupedParent?._childItems ?? [];

  return {
    children,
    parent,
    removalOrder: [...children, parent],
  };
}

/**
 * True when removing this configuration group leaves no product or gift-certificate
 * lines. Shopper Baskets has no multi-item DELETE; the empty-basket case can use
 * `deleteBasket` instead of N child-then-parent `removeItemFromBasket` calls.
 *
 * @param {Array<{itemId?: string}>} removalOrder
 * @param {Object|null|undefined} basket
 * @returns {boolean}
 */
export function removalGroupClearsBasket(removalOrder, basket) {
  const productItems = basket?.productItems ?? [];

  if (!productItems.length || !removalOrder?.length) {
    return false;
  }

  const removalIds = new Set(removalOrder.map((item) => item?.itemId).filter(Boolean));

  if (!removalIds.size) {
    return false;
  }

  const leftoverProducts = productItems.some((item) => !removalIds.has(item.itemId));
  const leftoverGiftCerts = (basket?.giftCertificateItems ?? []).length > 0;

  return !leftoverProducts && !leftoverGiftCerts;
}

/**
 * Maximum orderable quantity for a complex line item group.
 * Returns the minimum ATS across the parent product and all child products,
 * so the QuantityPicker cap is the lowest stock ceiling in the group.
 *
 * @param {Object} lineItem - Basket line item (may have _childItems)
 * @param {Object.<string, Object>} productsByItemId - itemId → SCAPI product
 * @returns {number} Minimum ATS, or Infinity if no inventory data is available
 */
export function getMaxQuantityForGroup(lineItem, productsByItemId) {
  const allItems = [lineItem, ...(lineItem._childItems ?? [])];
  const atsList = allItems
    .map((item) => productsByItemId[item.itemId]?.inventory?.ats)
    .filter((ats) => typeof ats === 'number');

  return atsList.length ? Math.min(...atsList) : Infinity;
}
